import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { ClientLayout, WeightChart } from "./ClientHome";
import { toast } from "sonner";
import { Upload, Trash2, Sparkles, Loader2, Ruler } from "lucide-react";

function CompareSlider({ beforeUrl, afterUrl, beforeLabel, afterLabel }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative w-full max-w-md aspect-[3/4] overflow-hidden rounded-2xl select-none" data-testid="compare-slider">
      <img src={afterUrl} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" />
      <img src={beforeUrl} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
      <span className="absolute top-3 left-3 kc-badge bg-background/85 text-foreground">{beforeLabel}</span>
      <span className="absolute top-3 right-3 kc-badge bg-primary text-primary-foreground">{afterLabel}</span>
      <div className="absolute inset-y-0 w-0.5 bg-white/90 pointer-events-none" style={{ left: `${pos}%` }} />
      <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))}
        aria-label="compare" data-testid="compare-slider-input"
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" />
    </div>
  );
}

export default function Photos() {
  const { t } = useLang();
  const [photos, setPhotos] = useState(null);
  const [kind, setKind] = useState("before");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [analyses, setAnalyses] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const analysisRef = useRef(null);
  const [measures, setMeasures] = useState([]);
  const [mForm, setMForm] = useState({ weight_kg: "", waist_cm: "", hips_cm: "", chest_cm: "", arm_cm: "", thigh_cm: "", body_fat: "", notes: "" });
  const [savingM, setSavingM] = useState(false);

  const load = () => api.get("/photos").then((r) => setPhotos(r.data));
  const loadAnalyses = () => api.get("/client/body-analysis").then((r) => setAnalyses(r.data)).catch(() => {});
  const loadMeasures = () => api.get("/client/measurements").then((r) => setMeasures(r.data)).catch(() => {});
  useEffect(() => { load(); loadAnalyses(); loadMeasures(); }, []);

  const saveMeasure = async () => {
    const payload = { notes: mForm.notes };
    for (const k of ["weight_kg", "waist_cm", "hips_cm", "chest_cm", "arm_cm", "thigh_cm", "body_fat"]) {
      if (mForm[k]) payload[k] = Number(String(mForm[k]).replace(",", "."));
    }
    if (Object.keys(payload).length === 1) {
      toast.error(t("ms.needOne"));
      return;
    }
    setSavingM(true);
    try {
      await api.post("/client/measurements", payload);
      toast.success(t("ms.saved"));
      setMForm({ weight_kg: "", waist_cm: "", hips_cm: "", chest_cm: "", arm_cm: "", thigh_cm: "", body_fat: "", notes: "" });
      loadMeasures();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingM(false);
    }
  };

  const delMeasure = async (id) => {
    await api.delete(`/client/measurements/${id}`);
    loadMeasures();
  };

  const analyze = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/client/body-analysis", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      loadAnalyses();
      toast.success(t("bf.result") + " ✓");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalyzing(false);
      if (analysisRef.current) analysisRef.current.value = "";
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("note", note);
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("ph.uploaded"));
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    load();
  };

  const grouped = { before: (photos || []).filter((p) => p.kind === "before"), after: (photos || []).filter((p) => p.kind === "after") };

  return (
    <ClientLayout>
      <div data-testid="photos-page">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-6">{t("ph.title")}</h1>

        <div className="kc-card p-5 mb-8 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="photo-upload-panel">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("ph.title")}</label>
            <div className="flex rounded-lg border border-border overflow-hidden mt-1">
              {["before", "after"].map((k) => (
                <button key={k} data-testid={`kind-${k}-button`} onClick={() => setKind(k)}
                  className={`px-4 py-2 text-sm font-bold ${kind === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                  {k === "before" ? t("ph.before") : t("ph.after")}
                </button>
              ))}
            </div>
          </div>
          <input data-testid="photo-note-input" placeholder={t("ph.note")} value={note}
            onChange={(e) => setNote(e.target.value)} className="kc-input flex-1" />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            data-testid="photo-file-input" onChange={upload} />
          <button data-testid="before-after-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="kc-btn-primary !py-2.5 flex items-center gap-2">
            <Upload className="w-4 h-4" /> {uploading ? t("common.loading") : t("ph.upload")}
          </button>
        </div>

        <section className="kc-card p-5 mb-8 kc-animate" data-testid="measurements-section">
          <p className="kc-eyebrow mb-3 flex items-center gap-2"><Ruler className="w-4 h-4" />{t("ms.title")}</p>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {[["weight_kg", "ms.weight"], ["waist_cm", "ms.waist"], ["hips_cm", "ms.hips"],
              ["chest_cm", "ms.chest"], ["arm_cm", "ms.arm"], ["thigh_cm", "ms.thigh"], ["body_fat", "ms.bf"]].map(([k, label]) => (
              <input key={k} data-testid={`ms-input-${k}`} inputMode="decimal" placeholder={t(label)}
                value={mForm[k]} onChange={(e) => setMForm({ ...mForm, [k]: e.target.value })}
                className="kc-input !px-3 font-mono text-center" />
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="ms-notes-input" placeholder={t("ph.note")} value={mForm.notes}
              onChange={(e) => setMForm({ ...mForm, notes: e.target.value })} className="kc-input flex-1" />
            <button data-testid="ms-save-button" onClick={saveMeasure} disabled={savingM} className="kc-btn-primary !py-2.5 !px-5">
              {savingM ? t("common.loading") : t("ms.add")}
            </button>
          </div>
          <WeightChart entries={measures} />
          {measures.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3" data-testid="ms-empty">{t("ms.empty")}</p>
          ) : (
            <ul className="divide-y divide-border mt-3" data-testid="ms-history">
              {measures.map((m, i) => {
                const oldest = measures[measures.length - 1];
                const delta = m.weight_kg != null && oldest?.weight_kg != null && i === 0
                  ? Math.round((m.weight_kg - oldest.weight_kg) * 10) / 10 : null;
                return (
                  <li key={m._id} className="py-2.5 flex items-center gap-3 text-sm group" data-testid={`ms-entry-${i}`}>
                    <span className="font-mono text-muted-foreground w-24 shrink-0">{m.date}</span>
                    <span className="flex-1 font-mono">
                      {[
                        m.weight_kg != null && `${m.weight_kg}kg`,
                        m.waist_cm != null && `V ${m.waist_cm}`,
                        m.hips_cm != null && `F ${m.hips_cm}`,
                        m.chest_cm != null && `P ${m.chest_cm}`,
                        m.arm_cm != null && `B ${m.arm_cm}`,
                        m.thigh_cm != null && `C ${m.thigh_cm}`,
                      ].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {delta != null && delta !== 0 && (
                      <span className={`kc-badge font-mono ${delta < 0 ? "bg-green-500/15 text-green-600" : "bg-primary/15 text-primary"}`}>
                        {delta > 0 ? "+" : ""}{delta}kg {t("ms.delta")}
                      </span>
                    )}
                    <button data-testid={`ms-delete-${i}`} onClick={() => delMeasure(m._id)}
                      className="text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="kc-card border-accent/50 p-5 mb-8 kc-animate" data-testid="bf-analysis-section">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <span className="w-12 h-12 rounded-full bg-accent/15 grid place-items-center shrink-0">
                <Sparkles className="w-6 h-6 text-accent" />
              </span>
              <div>
                <p className="font-bold">{t("bf.title")}</p>
                <p className="text-sm text-muted-foreground">{t("bf.subtitle")}</p>
              </div>
            </div>
            <input ref={analysisRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              data-testid="bf-file-input" onChange={analyze} />
            <button data-testid="bf-new-analysis-button" onClick={() => analysisRef.current?.click()} disabled={analyzing}
              className="kc-btn-primary !py-2.5 flex items-center justify-center gap-2">
              {analyzing ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t("bf.analyzing")}</>) : (<><Sparkles className="w-4 h-4" /> {t("bf.new")}</>)}
            </button>
          </div>
          {analyses.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4" data-testid="bf-empty">{t("bf.empty")}</p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="bf-results">
              <div className="bg-secondary/50 rounded-xl p-4" data-testid="bf-latest-result">
                <div className="flex items-end gap-2">
                  <span className="font-mono text-4xl font-black text-primary">
                    {analyses[0].bf_min}–{analyses[0].bf_max}%
                  </span>
                  <span className="text-xs text-muted-foreground pb-1.5">{t("bf.result")} · {analyses[0].created_at?.slice(0, 10)}</span>
                  <span className="kc-badge bg-accent/15 text-accent ml-auto">{t("bf.confidence")}: {analyses[0].confidence}</span>
                </div>
                {analyses[0].observations && (
                  <p className="text-sm text-muted-foreground mt-2" data-testid="bf-observations">
                    <span className="font-semibold text-foreground">{t("bf.observations")}: </span>{analyses[0].observations}
                  </p>
                )}
                <p className="text-xs text-muted-foreground/70 italic mt-2">{t("bf.disclaimer")}</p>
              </div>
              {analyses.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {analyses.slice(1).map((a) => (
                    <span key={a._id} className="kc-badge bg-secondary text-muted-foreground font-mono" data-testid="bf-history-item">
                      {a.created_at?.slice(0, 10)} · {a.bf_min}–{a.bf_max}%
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {grouped.before.length > 0 && grouped.after.length > 0 && (
          <section className="kc-card p-5 mb-8 kc-animate" data-testid="compare-section">
            <p className="kc-eyebrow mb-3">{t("ph.compare")}</p>
            <CompareSlider beforeUrl={fileUrl(grouped.before[0].storage_path)} afterUrl={fileUrl(grouped.after[0].storage_path)}
              beforeLabel={t("ph.before")} afterLabel={t("ph.after")} />
            <p className="text-xs text-muted-foreground mt-2 font-mono">
              {grouped.before[0].created_at?.slice(0, 10)} → {grouped.after[0].created_at?.slice(0, 10)}
            </p>
          </section>
        )}

        {!photos ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : photos.length === 0 ? (
          <p className="text-muted-foreground" data-testid="photos-empty">{t("ph.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {["before", "after"].map((k) => (
              <section key={k}>
                <h2 className={`text-lg font-bold mb-3 uppercase ${k === "before" ? "text-amber-500" : "text-green-500"}`}>
                  {k === "before" ? t("ph.before") : t("ph.after")}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {grouped[k].map((p) => (
                    <figure key={p._id} className="kc-card overflow-hidden relative group" data-testid="photo-card">
                      <img src={fileUrl(p.storage_path)} alt={k} className="w-full h-56 object-cover" loading="lazy" />
                      <figcaption className="p-2 text-xs flex justify-between items-center">
                        <span className="text-muted-foreground font-mono">{p.created_at?.slice(0, 10)}</span>
                        <button data-testid={`delete-photo-${p._id}`} onClick={() => remove(p._id)}
                          className="text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </figcaption>
                      {p.note && <p className="px-2 pb-2 text-xs text-muted-foreground italic">{p.note}</p>}
                    </figure>
                  ))}
                  {grouped[k].length === 0 && <p className="text-sm text-muted-foreground col-span-2">{t("ph.empty")}</p>}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
